import {
    createAsyncThunk, createSelector, createSlice, PayloadAction,
} from '@reduxjs/toolkit'

import * as PublicEmptyLegsApi from '~/services/api/public-empty-legs'

import { createEmptyPublicEmptyLegFilters } from '../application/public-empty-leg-filters'
import { executePublicEmptyLegSearch } from '../application/search-public-empty-legs'
import {
    PublicEmptyLegFilters,
    PublicEmptyLegFilterUpdate,
    PublicEmptyLegLoadStatus,
    PublicEmptyLegMarketSortType,
    PublicEmptyLegSearchResult,
    PublicEmptyLegSortType,
} from '../application/types'
import { buildPublicEmptyLegResults } from '../domain/presentation'
import { PublicEmptyLeg } from '../domain/types'
import type { RootState } from './store'

export type { PublicEmptyLegFilters } from '../application/types'
export type { PublicEmptyLeg } from '../domain/types'

export const getEmptyLegs = createAsyncThunk<PublicEmptyLegSearchResult, void, { state: RootState }>('table/getEmptyLegs', async (_, { getState }) => {
    const state = getState()
    const { selected, sortTab } = state.publicEmptyLegsTable

    return executePublicEmptyLegSearch(selected, sortTab, PublicEmptyLegsApi.searchPublicEmptyLegs)
})

export interface PublicEmptyLegTableState {
    sortTab: PublicEmptyLegMarketSortType
    emptyLegs: PublicEmptyLeg[]
    selected: PublicEmptyLegFilters
    appliedSearch: PublicEmptyLegFilters
    status: PublicEmptyLegLoadStatus
    error: string | null
    currentRequestId: string | null
}

const createInitialState = (): PublicEmptyLegTableState => ({
    sortTab: PublicEmptyLegSortType.LATEST,
    emptyLegs: [],
    status: 'idle',
    error: null,
    currentRequestId: null,
    appliedSearch: createEmptyPublicEmptyLegFilters(),
    selected: createEmptyPublicEmptyLegFilters(),
})

export const initialState: PublicEmptyLegTableState = createInitialState()

export const tableSlice = createSlice({
    name: 'publicEmptyLegsTable',
    initialState,
    reducers: {
        setSortTab: (state, { payload }: PayloadAction<PublicEmptyLegMarketSortType>) => {
            state.sortTab = payload
        },
        updateField: (state, { payload }: PayloadAction<PublicEmptyLegFilterUpdate>) => {
            switch (payload.field) {
                case 'from':
                    state.selected.from = payload.value
                    break
                case 'destination':
                    state.selected.destination = payload.value
                    break
                case 'from_location':
                    state.selected.from_location = payload.value
                    break
                case 'destination_location':
                    state.selected.destination_location = payload.value
                    break
                case 'dates':
                    state.selected.dates = payload.value
                    break
                default:
                    break
            }
        },
        resetSearch: createInitialState,
    },
    extraReducers: (builder) => {
        builder
            .addCase(getEmptyLegs.pending, (state, action) => {
                state.status = 'loading'
                state.error = null
                state.currentRequestId = action.meta.requestId
            })
            .addCase(getEmptyLegs.fulfilled, (state, action) => {
                if (state.currentRequestId !== action.meta.requestId) return

                state.emptyLegs = action.payload.emptyLegs
                state.appliedSearch = action.payload.search
                state.status = 'succeeded'
                state.currentRequestId = null
            })
            .addCase(getEmptyLegs.rejected, (state, action) => {
                if (state.currentRequestId !== action.meta.requestId) return

                state.status = 'failed'
                state.error = action.error.message || 'Unable to load empty legs'
                state.currentRequestId = null
            })
    },
})

export const { resetSearch, setSortTab, updateField } = tableSlice.actions

export const tableSelector = createSelector(
    (state: RootState) => state,
    (state) => state.publicEmptyLegsTable,
)
export const emptyLegsListSelector = createSelector(tableSelector, (table) => table?.emptyLegs || [])
export const publicEmptyLegResultsSelector = createSelector(
    emptyLegsListSelector,
    (state: RootState) => state.publicEmptyLegsTable.appliedSearch,
    buildPublicEmptyLegResults,
)
export const sortTabSelector = createSelector(tableSelector, (table) => table?.sortTab)
export const formDataSelector = createSelector(tableSelector, (table) => table?.selected)
export const appliedSearchSelector = createSelector(tableSelector, (table) => table?.appliedSearch)
export const emptyLegsStatusSelector = createSelector(tableSelector, (table) => table?.status)
export const emptyLegsErrorSelector = createSelector(tableSelector, (table) => table?.error)

export default tableSlice.reducer
